import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
// Replaced MUI pickers with custom range picker
import DateRangePicker from "../ui/DatePicker";
import useDebounce from "../../hooks/useDebounce";
import dayjs from 'dayjs';
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  X,
  BarChart3,
  Edit,
  Send,
  Eye,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Save,
  Trash2,
  MessageSquare,
  User,
  Calendar,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Tag,
  Target,
  TrendingUp,
  Zap,
  FileText,
  Instagram,
} from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { LayoutGrid, LayoutList } from 'lucide-react';

import toast from "react-hot-toast";
import ApiService from "../../services/ApiService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useBillingStore from "../../store/useBillingStore";
import { useConfirmDialog } from "../ui/Alert";
import FilterSidebar from "./FilterSidebar";
import { SupportQueryCardSkeleton, SupportQueryListSkeleton } from "../ui/SupportQuerySkeletons";

// --- TAG GENERATOR HELPER ---
const generateTagsFromNlpData = (query) => {
  const tags = [];
  try {
    // Add category tag
    if (query.nlp_data?.query_classification?.primary_category) {
      tags.push(query.nlp_data.query_classification.primary_category);
    }
    // Add urgency tag (only if high/critical)
    if (query.nlp_data?.urgency?.level &&
      ['high', 'critical'].includes(query.nlp_data.urgency.level.toLowerCase())) {
      tags.push(query.nlp_data.urgency.level);
    }
    // Add sentiment tag
    if (query.nlp_data?.sentiment?.overall) {
      tags.push(query.nlp_data.sentiment.overall);
    }
    // Add intent tag
    if (query.nlp_data?.query_classification?.intent) {
      tags.push(query.nlp_data.query_classification.intent);
    }
    // Add root cause category
    if (query.nlp_data?.root_cause?.category) {
      tags.push(query.nlp_data.root_cause.category);
    }
  } catch (error) {
    console.error('Error generating tags:', error);
  }
  return tags;
};

// Sentiment emoji mapping
const SENTIMENT_EMOJIS = {
  positive: '😊',
  neutral: '😐',
  negative: '😠',
  very_positive: '🤩',
  very_negative: '😡',
  frustrated: '😤',
  confused: '😕',
  satisfied: '😌',
  angry: '😠',
  happy: '😄',
};

const getSentimentEmoji = (sentiment) => {
  if (!sentiment) return '😐';
  const normalized = sentiment.toLowerCase().replace(/\s+/g, '_');
  return SENTIMENT_EMOJIS[normalized] || '😐';
};

// Urgency configuration
const URGENCY_CONFIG = {
  critical: {
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: '🔴',
    label: 'Critical'
  },
  high: {
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: '🟠',
    label: 'High'
  },
  medium: {
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: '🟡',
    label: 'Medium'
  },
  low: {
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: '🟢',
    label: 'Low'
  },
  default: {
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: '⚪',
    label: 'Normal'
  },
};

const getUrgencyConfig = (urgency) => {
  if (!urgency) return URGENCY_CONFIG.default;
  const normalized = urgency.toLowerCase();
  return URGENCY_CONFIG[normalized] || URGENCY_CONFIG.default;
};

// Standard black/gray colors for metadata
const METADATA_COLORS = {
  default: 'bg-white text-black border-gray-300',
  tag: 'bg-white text-black border-gray-300',
};

const getCategoryColor = (category) => {
  return METADATA_COLORS.default;
};

const getIntentColor = (intent) => {
  return METADATA_COLORS.default;
};

// Format label helper
const formatLabel = (text) => {
  if (!text) return '';
  return text
    .split(/[-_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};


function SupportQueries() {
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const { confirm } = useConfirmDialog();
  const [queries, setQueries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_previous: false
  });
  const [loading, setLoading] = useState(true);
  const { billingInfo, fetchBillingInfo } = useBillingStore();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [filters, setFilters] = useState({ status: ["pending"] });
  const [filterOptions, setFilterOptions] = useState(null);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedCurrentPageItem, setSelectedCurrentPageItem] = useState(false);

  const [itemsPerPage] = useState(10);
  const MAX_BULK = 50000;

  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    localStorage.setItem('loading', loading);
  }, [loading])

  const getCurrentUserName = () => {
    if (!user) return "Unknown User";
    if (user.fullName) return user.fullName;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.username) return user.username;
    if (user.emailAddresses?.[0]?.emailAddress) {
      const email = user.emailAddresses[0].emailAddress;
      const nameFromEmail = email.split('@')[0].replace(/[._-]/g, ' ');
      return nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
    }
    return "Current User";
  };

  const [showTestModal, setShowTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    name: "",
    email: "",
    mobile: "",
    query: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [manualResponseModal, setManualResponseModal] = useState({
    show: false,
    query: null,
    response: "",
    submitting: false,
    rewriting: false,
  });

  const [viewResponseModal, setViewResponseModal] = useState({
    show: false,
    response: '',
    query: null,
  });

  const [notesModal, setNotesModal] = useState({
    show: false,
    queryId: null,
    notes: [],
    newNote: "",
    submitting: false,
    editingNoteId: null,
    editingContent: "",
  });

  const [notesCache, setNotesCache] = useState(() => {
    try {
      const savedCache = localStorage.getItem('supportQueriesNotesCache');
      if (savedCache) {
        const parsedCache = JSON.parse(savedCache);
        return new Map(Object.entries(parsedCache));
      }
    } catch (error) {
      console.error('Failed to load notes cache from localStorage:', error);
    }
    return new Map();
  });

  useEffect(() => {
    try {
      const cacheObject = Object.fromEntries(notesCache);
      localStorage.setItem('supportQueriesNotesCache', JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Failed to save notes cache to localStorage:', error);
    }
  }, [notesCache]);

  const [listView, setListView] = useState(false);

  const usageLimitReached = billingInfo &&
    billingInfo.limits &&
    billingInfo.usage &&
    (billingInfo.usage.tokens >= billingInfo.limits.tokens ||
      billingInfo.usage.emails >= billingInfo.limits.emails);

  const getExceededDetails = () => {
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const asNumber = (v) => (typeof v === 'number' ? v : v == null ? NaN : Number(v));
    const details = [];
    const tokensUsed = asNumber(billingInfo.usage?.tokens);
    const tokensLimit = asNumber(billingInfo.limits?.tokens);
    if (!Number.isNaN(tokensUsed) && !Number.isNaN(tokensLimit) && tokensUsed >= tokensLimit) {
      details.push({
        key: 'tokens',
        title: 'AI Tokens',
        used: tokensUsed,
        limit: tokensLimit,
        why: "Your AI token quota is exhausted — automated AI responses are paused. Users won't be able to get AI-powered support queries from the widget. Please upgrade to restore this functionality."
      });
    }
    const emailsUsed = asNumber(billingInfo.usage?.emails);
    const emailsLimit = asNumber(billingInfo.limits?.emails);
    if (!Number.isNaN(emailsUsed) && !Number.isNaN(emailsLimit) && emailsUsed >= emailsLimit) {
      details.push({
        key: 'emails',
        title: 'Emails',
        used: emailsUsed,
        limit: emailsLimit,
        why: "Your monthly email quota is exhausted — support notification emails can't be sent. Please upgrade to restore email delivery."
      });
    }
    return details;
  };

  const [selectedQueries, setSelectedQueries] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  const [bulkReplyModal, setBulkReplyModal] = useState({
    show: false,
    reply: "",
    submitting: false,
    rewriting: false,
  });

  useEffect(() => {
    loadFilterOptions();
    fetchBillingInfo();
    cleanupNotesCache();

    // Sync search query state with current filters
    if (filters.search_query) {
      setSearchQuery(filters.search_query);
    }
  }, [filters.search_query]);

  // Sync debounced search query with filters
  useEffect(() => {
    setFilters(prev => {
      // Avoid infinite loop if value hasn't changed
      if (prev.search_query === debouncedSearchQuery) return prev;

      const newFilters = { ...prev };
      if (debouncedSearchQuery) {
        newFilters.search_query = debouncedSearchQuery;
      } else {
        delete newFilters.search_query;
      }
      return newFilters;
    });
  }, [debouncedSearchQuery]);

  const serializedFilters = useMemo(() => JSON.stringify(filters), [filters]);
  const serializedStartDate = useMemo(() => {
    if (!filterStartDate) return null;
    try {
      // Handle both dayjs objects and Date objects
      let formatted;
      if (filterStartDate.format && typeof filterStartDate.format === 'function') {
        formatted = filterStartDate.format('YYYY-MM-DD');
      } else {
        // If it's a Date object, convert to dayjs first
        formatted = dayjs(filterStartDate).format('YYYY-MM-DD');
      }
      // Validate the date is not the epoch date
      if (formatted === '1970-01-01') {
        console.warn('Invalid start date detected, returning null');
        return null;
      }
      return formatted;
    } catch (error) {
      console.error('Error serializing start date:', error);
      return null;
    }
  }, [filterStartDate]);

  const serializedEndDate = useMemo(() => {
    if (!filterEndDate) return null;
    try {
      // Handle both dayjs objects and Date objects
      let endDateObj = filterEndDate;
      if (filterEndDate.toDate && typeof filterEndDate.toDate === 'function') {
        endDateObj = filterEndDate.toDate();
      } else if (dayjs.isDayjs(filterEndDate)) {
        endDateObj = filterEndDate.toDate();
      }

      // Add one day to include the entire end date
      const nextDay = dayjs(endDateObj).add(1, 'day');
      const formatted = nextDay.format('YYYY-MM-DD');

      // Validate the date is not the epoch date
      if (formatted === '1970-01-01') {
        console.warn('Invalid end date detected, returning null');
        return null;
      }
      return formatted;
    } catch (error) {
      console.error('Error serializing end date:', error);
      return null;
    }
  }, [filterEndDate]);

  // --- FIXED LOAD QUERIES EFFECT ---
  useEffect(() => {
    const loadQueries = async () => {
      try {
        setLoading(true);

        // Validate date range - if start date exists, end date must also exist
        if (serializedStartDate && !serializedEndDate) {
          console.warn("Start date provided without end date, skipping date filter");
          toast.error("Please select both start and end dates");
          setLoading(false);
          return;
        }

        if (serializedEndDate && !serializedStartDate) {
          console.warn("End date provided without start date, skipping date filter");
          toast.error("Please select both start and end dates");
          setLoading(false);
          return;
        }

        const payload = {
          page: currentPage,
          per_page: itemsPerPage,
          filters: { ...filters },
        };

        // Only add date filters if both are present
        if (serializedStartDate && serializedEndDate) {
          payload.start_date = serializedStartDate;
          payload.end_date = serializedEndDate;
        }

        console.log("Loading queries with payload:", payload);

        const response = await ApiService.getSupportQueries(payload);
        console.log("Loaded queries data:", response);

        // Process data to ensure all metadata is available at root level
        const queriesWithTags = (response.data || []).map(query => ({
          ...query,
          // Fallback: If root property missing, try finding it in nlp_data
          classification: query.classification || query.nlp_data?.query_classification,
          sentiment: query.sentiment || query.nlp_data?.sentiment,
          urgency: query.urgency || query.nlp_data?.urgency,
          root_cause: query.root_cause || query.nlp_data?.root_cause,
          // Tags generation
          tags: (query.tags && query.tags.length > 0) ? query.tags : generateTagsFromNlpData(query)
        }));

        console.log("First query with generated tags:", queriesWithTags[0]?.tags);

        // Set the state ONCE with the processed data
        setQueries(queriesWithTags);

        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total_pages: response.pagination.total_pages || 1,
            total_items: response.pagination.total_items || 0,
            has_next: response.pagination.has_next || false,
            has_previous: response.pagination.has_previous || false
          }));
        }
      } catch (error) {
        console.error("Failed to load support queries:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        const errorMessage = error.response?.data?.message || error.message || "Failed to load support queries";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    loadQueries();
  }, [serializedFilters, currentPage, itemsPerPage, serializedStartDate, serializedEndDate, refreshTrigger]);

  useEffect(() => {
    console.log('Selected queries changed:', selectedQueries);
  }, [selectedQueries])

  // Clean up notes cache - remove entries older than 7 days
  const cleanupNotesCache = () => {
    try {
      const savedCache = localStorage.getItem('supportQueriesNotesCache');
      if (savedCache) {
        const parsedCache = JSON.parse(savedCache);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        const cleanedCache = {};
        Object.entries(parsedCache).forEach(([queryId, notes]) => {
          if (Array.isArray(notes) && notes.length > 0) {
            // Keep cache entries that have notes added within last 7 days
            const hasRecentNotes = notes.some(note => {
              const noteDate = new Date(note.added_at || note.updated_at);
              return noteDate > sevenDaysAgo;
            });
            if (hasRecentNotes) {
              cleanedCache[queryId] = notes;
            }
          }
        });

        localStorage.setItem('supportQueriesNotesCache', JSON.stringify(cleanedCache));
        setNotesCache(new Map(Object.entries(cleanedCache)));
      }
    } catch (error) {
      console.error('Failed to cleanup notes cache:', error);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const options = await ApiService.getSupportFilterOptions();
      setFilterOptions(options.filters);
    } catch (error) {
      console.error("Failed to load filter options:", error);
    }
  };

  // changes for download CSV
  const statusOptions = ["pending", "responded", "closed"];
  const [showDownloadCSVModal, setShowDownloadCSVModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const selectAll = () => setSelectedStatuses([...statusOptions]);
  const clearAll = () => setSelectedStatuses([]);

  const handleCheckboxToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleDownloadCSVSubmit = async (e) => {
    e.preventDefault();

    if (selectedStatuses.length === 0) {
      toast.error("Please apply the filters");
      return;
    }

    setSubmitting(true);

    try {
      const res = await ApiService.downloadCSVforSupportQueries(selectedStatuses, startDate, endDate);
      const recordsFound = res.headers["x-records-found"] || '0';

      if (recordsFound === '0') {
        toast.error("No matching records found!!");
        return;
      }
      const filename = res.headers["x-file-name"] || "support_queries.csv";
      console.log(filename);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      clearAll();
      setEndDate("");
      setStartDate("");
      setShowDownloadCSVModal(false);
      toast.success('File downloaded successfuly');
    }

    catch (error) {
      console.error("Test submission error:", error);
      toast.error("Failed to Download");
    }

    finally {
      setSubmitting(false);
    }
  }

  const handleTestSubmit = async (e) => {
    e.preventDefault();

    if (
      !testForm.name ||
      !testForm.email ||
      !testForm.mobile ||
      !testForm.query
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    // Check usage before submitting test query
    if (billingInfo && billingInfo.usage && billingInfo.limits && billingInfo.usage.emails >= billingInfo.limits.emails) {
      toast.error("You have reached your email limit. Please upgrade your plan.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await ApiService.submitSupportQuery(
        testForm.email,
        testForm.query,
        testForm.name,
        testForm.mobile
      );

      if (result.success) {
        toast.success("Test query submitted successfully!");
        setShowTestModal(false);
        setTestForm({ name: "", email: "", mobile: "", query: "" });
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error("Failed to submit test query");
      }
    } catch (error) {
      console.error("Test submission error:", error);
      toast.error("Failed to submit test query");
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualResponse = (query) => {
    // Prevent opening modal for closed queries
    if (query.status === 'closed') {
      toast.error("Cannot send manual response for closed queries");
      return;
    }

    setManualResponseModal({
      show: true,
      query: query,
      response: query.ai_response || "",
      submitting: false,
      rewriting: false,
    });
  };

  const submitManualResponse = async () => {
    if (!manualResponseModal.response.trim()) {
      toast.error("Please enter a response");
      return;
    }

    // Check usage before sending manual response
    if (billingInfo && billingInfo.usage && billingInfo.limits && billingInfo.usage.emails >= billingInfo.limits.emails) {
      toast.error("You have reached your email limit. Please upgrade your plan.");
      return;
    }

    setManualResponseModal((prev) => ({ ...prev, submitting: true }));

    try {
      const result = await ApiService.submitManualResponse(
        manualResponseModal.query.query_id,
        manualResponseModal.query.customer_email,
        manualResponseModal.response
      );

      if (result.success) {
        toast.success("Manual response sent successfully!");
        setManualResponseModal({
          show: false,
          query: null,
          response: "",
          submitting: false,
        });
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error("Failed to send manual response");
      }
    } catch (error) {
      console.error("Manual response error:", error);
      toast.error("Failed to send manual response");
    } finally {
      setManualResponseModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleRewriteManual = async () => {
    if (!manualResponseModal.response.trim()) {
      toast.error("Please enter a message to rewrite");
      return;
    }

    setManualResponseModal((prev) => ({ ...prev, rewriting: true }));

    try {
      const result = await ApiService.reWrite(manualResponseModal.response);

      // Support various API response shapes
      const rewrittenText = result?.refactored_text ||
        result?.refactoredText ||
        result?.rewritten_text ||
        result?.rewrittenText ||
        result?.output_text ||
        result?.outputText ||
        result?.corrected_text ||
        result?.correctedText ||
        result?.fixed_text ||
        result?.fixedText ||
        result?.text ||
        result?.message ||
        result?.content ||
        result?.result ||
        result?.data;

      if (rewrittenText && typeof rewrittenText === 'string' && rewrittenText.trim()) {
        setManualResponseModal((prev) => ({
          ...prev,
          response: rewrittenText,
          rewriting: false,
        }));
        toast.success("Message rewritten successfully!");
      } else {
        const availableFields = Object.keys(result || {}).join(', ');
        toast.error(`Failed to rewrite message - unexpected response format. Available fields: ${availableFields || 'none'}`);
        setManualResponseModal((prev) => ({ ...prev, rewriting: false }));
      }
    } catch (error) {
      console.error("Rewrite error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || error.message || "Failed to rewrite message");
      setManualResponseModal((prev) => ({ ...prev, rewriting: false }));
    }
  };

  const handleCloseQuery = async (queryId) => {
    try {
      const result = await ApiService.updateSupportQuery([queryId], "closed");
      if (result.success) {
        toast.success("Query closed successfully!");
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error("Failed to close query");
      }
    } catch (error) {
      console.error("Close query error:", error);
      toast.error(error.message || "Failed to close query");
    }
  };

  const handleReopenQuery = async (queryId) => {
    try {
      const result = await ApiService.updateSupportQuery([queryId], "pending");
      if (result.success) {
        toast.success("Query reopened successfully!");
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error("Failed to reopen query");
      }
    } catch (error) {
      console.error("Reopen query error:", error);
      toast.error(error.message || "Failed to reopen query");
    }
  };

  // Bulk reply functions
  const handleOpenBulkReplyModal = () => {
    if (selectedQueries.size === 0) {
      toast.error("Please select at least one query to reply");
      return;
    }
    if (selectedQueries.size > MAX_BULK) {
      toast.error(`we can reply ${MAX_BULK} queries at once`);
      return;
    }

    setBulkReplyModal({
      show: true,
      reply: "",
      submitting: false,
    });
  };

  const handleCloseBulkReplyModal = () => {
    setBulkReplyModal({
      show: false,
      reply: "",
      submitting: false,
      rewriting: false,
    });
  };

  const submitBulkReply = async () => {
    if (!bulkReplyModal.reply.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    if (selectedQueries.size === 0) {
      toast.error("No queries selected");
      return;
    }

    if (selectedQueries.size > MAX_BULK) {
      toast.error(`we can reply ${MAX_BULK} queries at once`);
      return;
    }

    // Check usage before sending bulk reply
    const emailsToSend = selectedQueries.size;
    if (billingInfo && billingInfo.usage && billingInfo.limits) {
      const remainingEmails = billingInfo.limits.emails - billingInfo.usage.emails;
      if (remainingEmails < emailsToSend) {
        toast.error(`Insufficient email quota. You need ${emailsToSend} emails but only have ${remainingEmails} remaining. Please upgrade your plan.`);
        return;
      }
    }

    setBulkReplyModal((prev) => ({ ...prev, submitting: true }));

    try {
      const queryIds = Array.from(selectedQueries);
      const result = await ApiService.submitReplyAll(queryIds, bulkReplyModal.reply);

      if (result.success) {
        toast.success(`Successfully sent reply to ${queryIds.length} ${queryIds.length === 1 ? 'query' : 'queries'}!`);

        // Clear selection and close modal
        setSelectedQueries(new Set());
        setIsSelectAllChecked(false);
        setBulkReplyModal({
          show: false,
          reply: "",
          submitting: false,
          rewriting: false,
        });

        // Reload queries to update statuses
        setRefreshTrigger(prev => prev + 1);
        fetchBillingInfo(); // Update billing info to reflect email usage
      } else {
        toast.error(result.message || "Failed to send bulk reply");
      }
    } catch (error) {
      console.error("Bulk reply error:", error);
      toast.error(error.message || "Failed to send bulk reply");
    } finally {
      setBulkReplyModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleRewriteReply = async () => {
    if (!bulkReplyModal.reply.trim()) {
      toast.error("Please enter a message to rewrite");
      return;
    }

    setBulkReplyModal((prev) => ({ ...prev, rewriting: true }));

    try {
      const result = await ApiService.reWrite(bulkReplyModal.reply);

      console.log("Rewrite API full response:", JSON.stringify(result, null, 2));
      console.log("Response keys:", Object.keys(result || {}));

      // Check for different possible response field names
      const rewrittenText = result?.refactored_text ||  // Primary field from API
        result?.refactoredText ||
        result?.rewritten_text ||
        result?.rewrittenText ||
        result?.output_text ||
        result?.outputText ||
        result?.corrected_text ||
        result?.correctedText ||
        result?.fixed_text ||
        result?.fixedText ||
        result?.text ||
        result?.message ||
        result?.content ||
        result?.result ||
        result?.data;

      if (rewrittenText && typeof rewrittenText === 'string' && rewrittenText.trim()) {
        setBulkReplyModal((prev) => ({
          ...prev,
          reply: rewrittenText,
          rewriting: false,
        }));
        toast.success("Message rewritten successfully!");
      } else {
        console.error("Unexpected response format. Full result:", result);
        console.error("All response fields:", result);

        // Try to show what fields are available
        const availableFields = Object.keys(result || {}).join(', ');
        toast.error(`Failed to rewrite message - unexpected response format. Available fields: ${availableFields || 'none'}`);
        setBulkReplyModal((prev) => ({ ...prev, rewriting: false }));
      }
    } catch (error) {
      console.error("Rewrite error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || error.message || "Failed to rewrite message");
      setBulkReplyModal((prev) => ({ ...prev, rewriting: false }));
    }
  };

  // Bulk selection functions
  const handleSelectQuery = (queryId) => {
    const newSelected = new Set(selectedQueries);
    if (newSelected.has(queryId)) {
      newSelected.delete(queryId);
    } else {
      // Enforce MAX_BULK cap
      if (newSelected.size >= MAX_BULK) {
        toast.error(`we can reply ${MAX_BULK} queries at once`);
      } else {
        newSelected.add(queryId);
      }
    }
    setSelectedQueries(newSelected);
    setSelectedCurrentPageItem(true);

    // Update select all checkbox state - check if ALL selectable (across search up to MAX_BULK) are selected
    const selectableIds = queries.slice(0, MAX_BULK).map(q => q.query_id);
    setIsSelectAllChecked(selectableIds.length > 0 && selectableIds.every(id => newSelected.has(id)));
  };

  const handleSelectAll = () => {
    // Select ALL queries across current search results up to MAX_BULK
    const selectableIds = queries.slice(0, MAX_BULK).map(q => q.query_id);
    if (isSelectAllChecked) {
      // Deselect all selectable ids
      const newSelected = new Set(selectedQueries);
      selectableIds.forEach(id => newSelected.delete(id));
      setSelectedQueries(newSelected);
      setIsSelectAllChecked(false);
      setSelectedCurrentPageItem(false);
    } else {
      const newSelected = new Set(selectedQueries);
      selectableIds.forEach(id => newSelected.add(id));
      setSelectedQueries(newSelected);
      setIsSelectAllChecked(true);
      setSelectedCurrentPageItem(true);
    }
  };

  const handleDeselectCurrentPage = () => {
    if (!currentQueries || currentQueries.length === 0) return;
    const idsOnPage = currentQueries.map(q => q.query_id);
    const newSelected = new Set(selectedQueries);
    idsOnPage.forEach(id => newSelected.delete(id));
    setSelectedQueries(newSelected);
    const selectableIds = queries.slice(0, MAX_BULK).map(q => q.query_id);
    setIsSelectAllChecked(selectableIds.length > 0 && selectableIds.every(id => newSelected.has(id)));
    setSelectedCurrentPageItem(false);
  };

  const handleTogglePageSelection = () => {
    if (!currentQueries || currentQueries.length === 0) return;
    const idsOnPage = currentQueries.map(q => q.query_id);
    const newSelected = new Set(selectedQueries);
    if (isPageAllSelected) {
      idsOnPage.forEach(id => newSelected.delete(id));
    } else {
      for (const id of idsOnPage) {
        if (newSelected.size >= MAX_BULK) break;
        newSelected.add(id);
      }
    }
    setSelectedQueries(newSelected);
    const selectableIds = queries.slice(0, MAX_BULK).map(q => q.query_id);
    setIsSelectAllChecked(selectableIds.length > 0 && selectableIds.every(id => newSelected.has(id)));
  };

  const handleBulkClose = async () => {
    if (selectedQueries.size === 0) {
      toast.error("Please select queries to close");
      return;
    }

    const result = await confirm({
      title: 'Close Selected Queries',
      message: `Are you sure you want to close ${selectedQueries.size} selected queries?`,
      type: 'warning',
      confirmText: 'Close',
      cancelText: 'Cancel',
    });

    if (!result) {
      return;
    }

    try {
      const queryIds = Array.from(selectedQueries);
      const result = await ApiService.updateSupportQuery(queryIds, "closed");
      const successCount = result.modified_count;

      if (successCount > 0) {
        toast.success(`Successfully closed ${successCount} queries`);
      }

      // Clear selection and reload queries
      setSelectedQueries(new Set());
      setIsSelectAllChecked(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Bulk close error:", error);
      toast.error("Failed to close selected queries");
    }
  };

  const handleClosedQueriesReopen = async () => {
    if (selectedQueries.size === 0) {
      toast.error("Please select queries to close");
      return;
    }

    const result = await confirm({
      title: 'Reopen All Closed Queries',
      message: `Are you sure you want to reopen all closed queries?`,
      type: 'info',
      confirmText: 'Reopen',
      cancelText: 'Cancel',
    });

    if (!result) {
      return;
    }

    try {
      const closedQueryIds = queries
        .filter((data) => data.status === "closed" && selectedQueries.has(data.query_id))
        .map((data) => data.query_id);

      if (closedQueryIds.length === 0) {
        toast.error("There are no closed queries to reopen");
        return;
      }

      console.log('closedQueryIds', closedQueryIds);
      const result = await ApiService.updateSupportQuery(closedQueryIds, "pending");
      const successCount = result.modified_count;

      if (successCount > 0) {
        toast.success(`Successfully reopened ${successCount} queries`);
      }

      // Clear selection and reload queries
      setSelectedQueries(new Set());
      setIsSelectAllChecked(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Bulk reopen error:", error);
      toast.error("Failed to reopen selected queries");
    }
  }

  // Notes management functions
  const handleAddNote = async () => {
    if (!notesModal.newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));

      const result = await ApiService.addNoteToSupportQuery(notesModal.queryId, notesModal.newNote);

      if (result.success) {
        toast.success("Note added successfully!");

        // Create new note object with unique ID
        const newNoteObj = {
          id: `temp_${Date.now()}_${Math.random()}`, // More unique temporary ID
          note: notesModal.newNote,
          added_by: getCurrentUserName(), // Use current Clerk user
          added_at: new Date().toISOString(),
        };

        // Get existing notes from cache for this query
        const existingCachedNotes = notesCache.get(notesModal.queryId) || [];
        const updatedCachedNotes = [...existingCachedNotes, newNoteObj];

        // Update persistent cache
        setNotesCache(prevCache => {
          const newCache = new Map(prevCache);
          newCache.set(notesModal.queryId, updatedCachedNotes);
          return newCache;
        });

        // Add new note to modal state immediately
        setNotesModal((prev) => ({
          ...prev,
          notes: [...prev.notes, newNoteObj],
          newNote: "",
          submitting: false,
        }));

        // Update the local queries state to persist notes across modal openings
        setQueries(prevQueries =>
          prevQueries.map(query => {
            if (query.query_id === notesModal.queryId) {
              const updatedNotes = query.notes ?
                (Array.isArray(query.notes) ? [...query.notes, newNoteObj] : [query.notes, newNoteObj]) :
                [newNoteObj];
              return {
                ...query,
                notes: updatedNotes
              };
            }
            return query;
          })
        );

        // Reload queries in background without affecting current modal state
        // setRefreshTrigger(prev => prev + 1); // No need to reload for notes, local update is fine

      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      console.error("Add note error:", error);
      toast.error(error.message || "Failed to add note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleEditNote = (noteId, currentContent) => {
    setNotesModal(prev => ({
      ...prev,
      editingNoteId: noteId,
      editingContent: currentContent
    }));
  };

  const handleSaveEditNote = async (noteId) => {
    if (!notesModal.editingContent.trim()) {
      toast.error("Note content cannot be empty");
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));

      // Here you would call an API to update the specific note
      const updatedNotes = notesModal.notes.map(note =>
        note.id === noteId
          ? { ...note, note: notesModal.editingContent, updated_at: new Date().toISOString() }
          : note
      );

      await ApiService.addNoteToSupportQuery(notesModal.queryId, updatedNotes);

      // Update cached notes
      setNotesCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(notesModal.queryId, updatedNotes);
        return newCache;
      });

      setNotesModal((prev) => ({
        ...prev,
        notes: updatedNotes,
        editingNoteId: null,
        editingContent: "",
        submitting: false,
      }));

      toast.success("Note updated successfully!");
    } catch (error) {
      console.error("Update note error:", error);
      toast.error(error.message || "Failed to update note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };
  const handleDeleteNote = async (noteId) => {
    const results = await confirm({
      title: 'Delete Note',
      message: `Are you sure you want to delete this note?`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!results) {
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));

      // Here you would call an API to delete the specific note
      const updatedNotes = notesModal.notes.filter(note => note.id !== noteId);
      await ApiService.addNoteToSupportQuery(notesModal.queryId, updatedNotes);

      // Update cached notes
      setNotesCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(notesModal.queryId, updatedNotes);
        return newCache;
      });

      setNotesModal((prev) => ({
        ...prev,
        notes: updatedNotes,
        submitting: false,
      }));

      toast.success("Note deleted successfully!");
    } catch (error) {
      console.error("Delete note error:", error);
      toast.error(error.message || "Failed to delete note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleCancelEdit = () => {
    setNotesModal(prev => ({
      ...prev,
      editingNoteId: null,
      editingContent: ""
    }));
  };

  const openNotesModal = async (queryId) => {
    try {
      console.log(`Opening notes modal for query ${queryId}`);
      console.log('Current notes cache:', Object.fromEntries(notesCache));

      // First priority: Check cached notes for this query
      let existingNotes = [];
      const cachedNotes = notesCache.get(queryId);

      if (cachedNotes && cachedNotes.length > 0) {
        console.log(`Found ${cachedNotes.length} cached notes for query ${queryId}:`, cachedNotes);
        existingNotes = cachedNotes;
      } else {
        console.log(`No cached notes found for query ${queryId}, checking other sources...`);
        // Second priority: Check if modal already has notes for this query
        if (notesModal.queryId === queryId && notesModal.notes.length > 0) {
          console.log(`Using existing modal notes for query ${queryId}: ${notesModal.notes.length} notes`);
          existingNotes = notesModal.notes;
          // Cache these notes for future use
          setNotesCache(prevCache => {
            const newCache = new Map(prevCache);
            newCache.set(queryId, notesModal.notes);
            return newCache;
          });
        } else {
          // Third priority: Extract from local queries state
          const query = queries.find(q => q.query_id === queryId);
          console.log(`Query data for ${queryId}:`, query);

          // Helper function to process a single note
          const processNote = (noteData, index = 0) => {
            if (!noteData) return null;

            if (typeof noteData === 'string') {
              return {
                id: `note_${queryId}_${index}_${Date.now()}`,
                note: noteData,
                added_by: getCurrentUserName(), // Use current Clerk user instead of "Unknown User"
                added_at: new Date().toISOString()
              };
            } else if (typeof noteData === 'object' && noteData.note) {
              return {
                id: noteData.id || `note_${queryId}_${index}_${Date.now()}`,
                note: noteData.note,
                added_by: noteData.added_by || getCurrentUserName(), // Use current Clerk user as fallback
                added_at: noteData.added_at || new Date().toISOString(),
                updated_at: noteData.updated_at
              };
            }
            return null;
          };

          // Process notes from different possible fields
          if (query?.notes) {
            if (Array.isArray(query.notes)) {
              // Handle array of notes
              existingNotes = query.notes
                .map((note, index) => processNote(note, index))
                .filter(note => note !== null);
            } else {
              // Handle single note object or string
              const processedNote = processNote(query.notes, 0);
              if (processedNote) existingNotes = [processedNote];
            }
          } else if (query?.note) {
            // Handle legacy single note field
            const processedNote = processNote(query.note, 0);
            if (processedNote) existingNotes = [processedNote];
          }

          // Cache the processed notes
          if (existingNotes.length > 0) {
            console.log(`Caching ${existingNotes.length} processed notes for query ${queryId}`);
            setNotesCache(prevCache => {
              const newCache = new Map(prevCache);
              newCache.set(queryId, existingNotes);
              return newCache;
            });
          }

          // Last resort: Fetch from server only if no notes found anywhere
          if (existingNotes.length === 0) {
            // Removed causing error
          }
        }
      }

      console.log(`Final notes count for query ${queryId}: ${existingNotes.length} notes`, existingNotes);

      setNotesModal({
        show: true,
        queryId: queryId,
        notes: existingNotes,
        newNote: "",
        submitting: false,
        editingNoteId: null,
        editingContent: "",
      });
    } catch (error) {
      console.error("Error opening notes modal:", error);
      setNotesModal({
        show: true,
        queryId: queryId,
        notes: [],
        newNote: "",
        submitting: false,
        editingNoteId: null,
        editingContent: "",
      });
    }
  };

  // const shouldShowManualButton = (query) => {
  //   // Show manual button if:
  //   // 1. No AI response was generated OR
  //   // 2. need_to_escalate is true
  //   return !query.ai_response || query.need_to_escalate === true;
  // };
  const getStatusIcon = (status) => {
    switch (status) {
      case "responded":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "pending":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case "closed":
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "responded":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  // Memoize status counts - removed as we are now server-side
  // We can only show total items from pagination
  const statusCounts = {
    total: pagination.total_items,
    responded: 0, // Not available from this API endpoint
    pending: 0,   // Not available from this API endpoint
    closed: 0     // Not available from this API endpoint
  };

  // Removed StatsCard component as we don't have individual counts anymore
  // const StatsCard = ...

  // Use queries directly as they are now server-side filtered
  const currentQueries = queries;
  const totalItems = pagination.total_items;
  const totalPages = pagination.total_pages;
  const isPageAllSelected = currentQueries.length > 0 && currentQueries.every(q => selectedQueries.has(q.query_id));

  // Pagination handlers updated to use setPagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (pagination.has_previous) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // Sync search query if changed in filters sidebar
    if (newFilters.search_query !== undefined) {
      setSearchQuery(newFilters.search_query || "");
    }
  }, []);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    // useEffect will trigger reload when filters change via handleFilterChange
    setIsFilterSidebarOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({ status: ["pending"] }); // Reset to default view
    setSearchQuery("");
    setCurrentPage(1);
  };

  useEffect(() => {
    // console.log('here is the searchParams searchParams', searchParams.get('search_query'));
    if (loading === false && searchParams.get('search_query') !== null && queries?.length > 5) {
      console.log('here is the queries queries', queries, queries.length);
      setSearchQuery(searchParams.get('search_query'));
    }
  }, [searchParams, loading, queries])

  if (loading) {
    return (
      <div className="space-y-4 px-2 sm:px-0 min-h-[90vh]">
        {/* Header Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Filters Skeleton */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-full bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="h-12 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="h-12 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* List/Card Skeleton */}
        {listView ? (
          <SupportQueryListSkeleton />
        ) : (
          <SupportQueryCardSkeleton />
        )}
      </div>
    );
  }


  return (
    <div className="space-y-3 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        {/* Title Section */}
        <div className="flex-shrink-0">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Support Queries
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
            Manage customer support queries with AI-powered responses
          </p>
        </div>

        {/* Action Buttons - Top Right Corner */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center lg:ml-auto flex-shrink-0">
          {/* Date Range Filter */}
          <div className="relative w-full sm:w-auto sm:min-w-[200px] z-20">
            <DateRangePicker
              startDate={filterStartDate ? filterStartDate.toDate ? filterStartDate.toDate() : filterStartDate : null}
              endDate={filterEndDate ? filterEndDate.toDate ? filterEndDate.toDate() : filterEndDate : null}
              onChange={(since, until) => {
                setFilterStartDate(since ? dayjs(since) : null);
                setFilterEndDate(until ? dayjs(until) : null);
              }}
              className="w-full"
            />
          </div>

          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <button
              onClick={() => setShowDownloadCSVModal(true)}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Download CSV</span>
              <span className="sm:hidden">Download</span>
            </button>
            <button
              onClick={() => setShowTestModal(true)}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Test Query</span>
              <span className="sm:hidden">Test</span>
            </button>


          </div>
        </div>
      </div>
      {(() => {
        const exceeded = getExceededDetails();
        if (!exceeded || exceeded.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-red-900">
                      Usage limit reached
                    </h4>
                    <p className="text-sm text-red-700/90 mt-0.5">
                      You've reached the following monthly quota{exceeded.length > 1 ? 's' : ''}. Upgrade to increase limits and re-enable affected features.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-red-700 transition-colors"
                  >
                    View billing & upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-red-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-red-700/80">
                            {d.title}
                          </span>
                          <span className="text-[11px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-red-100 border border-red-200 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-600"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-red-700/90 leading-relaxed">
                          {d.why}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Filters - Improved Full-Width Layout */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 relative z-20">
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-center w-full gap-3">
            {/* Main Search Bar */}
            <div className="w-full sm:flex-1 relative">
              <input
                type="text"
                placeholder="Search queries by ticket ID, name, email, query text..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 sm:h-14 pl-11 pr-4 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-black/20 focus:border-black transition-all shadow-sm"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Advanced Filters and View Toggle - Grouped on mobile */}
            <div className="flex items-center w-full sm:w-auto gap-3">
              {/* Advanced Filters Button */}
              <button
                onClick={() => setIsFilterSidebarOpen(true)}
                className="flex-1 sm:flex-none relative flex items-center justify-center h-12 sm:h-14 px-4 sm:px-6 bg-white border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-all duration-200 text-sm font-medium shrink-0"
              >
                <Filter className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-500" />
                <span className="hidden sm:inline">Filters</span>
                {Object.keys(filters).length > 0 && (
                  <span className="ml-2 bg-gray-100 text-black text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>

              {/* View Toggle Button */}
              <button
                type="button"
                onClick={() => setListView((prev) => !prev)}
                className={`flex-1 sm:flex-none relative flex items-center justify-center h-12 sm:h-14 px-4 sm:px-6 rounded-lg border border-gray-300 shadow-sm transition-all duration-200 whitespace-nowrap text-sm font-medium ${listView
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                title={listView ? 'Switch to Card View' : 'Switch to List View'}
              >
                {listView ? (
                  <LayoutGrid className="h-4 w-4 sm:mr-2" />
                ) : (
                  <LayoutList className="h-4 w-4 sm:mr-2" />
                )}
                <span className="hidden sm:inline">
                  {listView ? 'Card View' : 'List View'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        filterOptions={filterOptions}
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />


      {/* Queries List */}
      {currentQueries.length === 0 ? (
        <div className="text-center py-16">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No support queries found
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            Try adjusting your search or filter settings
          </p>
        </div>
      ) : (
        <>
          {/* Quick Select All Button - Always visible */}
          {currentQueries.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={handleSelectAll}
                  className={`flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${isSelectAllChecked
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-white text-black border-2 border-black hover:bg-gray-50'
                    }`}
                >
                  <CheckCircle className="h-4 w-4 mr-1 sm:mr-2" />
                  {totalItems > MAX_BULK
                    ? isSelectAllChecked
                      ? `Deselect First ${MAX_BULK}`
                      : `Select First ${MAX_BULK}`
                    : isSelectAllChecked
                      ? `Deselect All (${totalItems})`
                      : `Select All (${totalItems})`}
                </button>
                {
                  selectedCurrentPageItem && <button
                    onClick={handleDeselectCurrentPage}
                    className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50"
                    title="Deselect all items on this page"
                  >
                    Deselect Page ({currentQueries.length})
                  </button>
                }
              </div>
              {selectedQueries.size > 0 && (
                <span className="text-xs sm:text-sm text-gray-600 w-full sm:w-auto text-left sm:text-right">
                  {selectedQueries.size} query{selectedQueries.size !== 1 ? 'ies' : ''} selected
                </span>
              )}
            </div>
          )}
          {totalItems > MAX_BULK && (
            <div className="mb-4 text-xs sm:text-sm text-red-600 px-2">we can reply 500 queries at once</div>
          )}

          {/* Bulk Actions Bar */}
          {selectedQueries.size > 0 && (
            <div className="mb-4 p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <span className="text-xs sm:text-sm font-medium text-blue-900">
                    {selectedQueries.size} query{selectedQueries.size !== 1 ? 'ies' : ''} selected
                  </span>
                  <button
                    onClick={() => {
                      setSelectedQueries(new Set());
                      setIsSelectAllChecked(false);
                      setSelectedCurrentPageItem(false);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">

                  <button
                    onClick={() => handleClosedQueriesReopen()}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
                    title="Reopen All Closed Queries"
                  >
                    Reopen
                  </button>
                  <button
                    onClick={handleOpenBulkReplyModal}
                    className="flex-1 sm:flex-none flex items-center justify-center px-3 sm:px-4 py-2 bg-black text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Send className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Reply All Selected</span>
                    <span className="sm:hidden">Reply All</span>
                  </button>
                  <button
                    onClick={handleBulkClose}
                    className="flex-1 sm:flex-none flex items-center justify-center px-3 sm:px-4 py-2 bg-red-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Close Selected</span>
                    <span className="sm:hidden">Close</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {listView ? (
            // List View Table
            <div className="overflow-x-auto w-full ">
              <table className="min-w-full bg-white rounded-xl shadow-md text-xs md:text-sm">
                <thead>
                  <tr>
                    <th className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isPageAllSelected}
                        onChange={handleTogglePageSelection}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Ticket ID</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">Status</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Date</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Name</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Email</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Mobile</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Query</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Metadata</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Reply from AI Support Agent</th>
                    <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentQueries.map((query) => (
                    <tr key={query.query_id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-2 align-top text-center">
                        <input
                          type="checkbox"
                          checked={selectedQueries.has(query.query_id)}
                          onChange={() => handleSelectQuery(query.query_id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 md:px-4 py-2 align-top text-left font-medium text-blue-600 whitespace-nowrap">
                        #{query.ticket_id || query.query_id || 'N/A'}
                      </td>
                      <td className="px-2 md:px-4 py-2 align-top text-center min-w-[90px] font-semibold">
                        <div className="flex flex-col items-center gap-1 text-center">
                          <span className={`inline-flex items-center justify-center px-2 py-1 min-w-[80px] h-7 rounded-full text-xs font-medium shadow-sm text-center ${getStatusColor(query.status)}`}>{query.status.toLowerCase()}</span>
                          {(query.status === 'pending' || query.status === 'responded' || query.status === 'closed') && (
                            <button
                              className="flex items-center justify-center px-2 py-1 min-w-[80px] h-7 rounded-full text-xs font-medium bg-transparent text-gray-700 hover:bg-gray-300 transition text-center"
                              title="View Response"
                              onClick={() => setViewResponseModal({
                                show: true,
                                response: query.ai_response || query.response || (query.human_response && query.human_response.join('\n\n')) || '',
                                query,
                              })}
                            >
                              <Eye className="h-4 w-4 mr-1" /> View
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-2 align-top text-gray-600 min-w-[110px] whitespace-nowrap ">
                        {new Date(query.created_at).toLocaleDateString()}<br />
                        {new Date(query.created_at).toLocaleTimeString()}
                      </td>
                      <td className="px-2 md:px-4 py-2 align-top text-gray-900 min-w-[100px] whitespace-nowrap font-medium">{query.name || 'N/A'}</td>
                      <td className="px-2 md:px-4 py-2 align-top break-all text-gary-600 min-w-[120px] ">{query.customer_email}</td>
                      <td className="px-2 md:px-4 py-2 align-top text-gray-600 min-w-[90px] whitespace-nowrap ">{query.mobile || 'N/A'}</td>
                      <td className="px-2 md:px-4 py-2 align-top text-gray-600 max-w-[120px] truncate min-w-[120px] " title={query.query}>{query.query}</td>
                      <td className="px-2 md:px-4 py-2 align-top min-w-[150px]">
                        {(() => {
                          const metadata = [];

                          if (query.external_integration?.instagram) {
                            metadata.push({
                              type: 'instagram_source',
                              value: 'Instagram',
                              label: 'Instagram',
                              color: 'bg-gray-100 border-gray-300 text-black'
                            });
                          }

                          // --- FIX: MOVE TAGS TO THE TOP ---
                          // We add tags first so they appear first and aren't cut off by maxDisplay
                          if (query.tags && Array.isArray(query.tags) && query.tags.length > 0) {
                            query.tags.forEach(tag => {
                              metadata.push({
                                type: 'tag',
                                value: tag,
                                label: tag,
                                color: METADATA_COLORS.tag
                              });
                            });
                          }

                          // Then add the rest of the metadata
                          if (query.urgency?.level) {
                            const config = getUrgencyConfig(query.urgency.level);
                            metadata.push({
                              type: 'urgency',
                              value: config.icon,
                              label: config.label,
                              color: config.color
                            });
                          }

                          if (query.sentiment?.emotion || query.sentiment?.overall) {
                            const sentiment = query.sentiment.emotion || query.sentiment.overall;
                            metadata.push({
                              type: 'sentiment',
                              value: getSentimentEmoji(sentiment),
                              label: formatLabel(sentiment),
                              color: 'bg-gray-50 border-gray-200'
                            });
                          }

                          if (query.classification?.primary_category) {
                            metadata.push({
                              type: 'category',
                              value: formatLabel(query.classification.primary_category),
                              label: formatLabel(query.classification.primary_category),
                              color: getCategoryColor(query.classification.primary_category)
                            });
                          }

                          if (query.classification?.intent) {
                            metadata.push({
                              type: 'intent',
                              value: formatLabel(query.classification.intent),
                              label: formatLabel(query.classification.intent),
                              color: getIntentColor(query.classification.intent)
                            });
                          }

                          if (metadata.length === 0) {
                            return <span className="text-xs text-gray-400">-</span>;
                          }

                          // Optional: Increase this to 4 if you have room
                          const maxDisplay = 5;
                          const displayItems = metadata.slice(0, maxDisplay);
                          const remainingCount = metadata.length - maxDisplay;

                          return (
                            <div className="flex flex-wrap gap-1 items-center">
                              {displayItems.map((item, index) => (
                                <span
                                  key={`${item.type}-${index}`}
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${item.color}`}
                                  title={`${item.type}: ${item.label}`}
                                >
                                  {/* Ensure the Tag icon renders specifically for 'tag' type */}
                                  {item.type === 'tag' && <Tag className="h-2.5 w-2.5 mr-0.5" />}
                                  {item.type === 'instagram_source' && <Instagram className="h-2.5 w-2.5 mr-0.5" />}
                                  {item.value}
                                </span>
                              ))}
                              {remainingCount > 0 && (
                                <span className="text-[10px] text-gray-500 font-medium px-1.5 py-0.5 bg-gray-50 rounded border border-gray-200">
                                  +{remainingCount}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 md:px-4 py-2 align-top text-gray-600 max-w-[120px] truncate min-w-[120px] " title={query.ai_response || query.response}>
                        {(query.ai_response || query.response) ? (query.ai_response || query.response) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-2 md:px-6 py-2 md:py-4 align-top min-w-[90px] md:min-w-[120px] font-semibold">
                        <div className="flex flex-col space-y-1 md:justify-center md:items-center">
                          {query.external_integration?.instagram && (
                            <button
                              onClick={() => {
                                const ig = query.external_integration.instagram;
                                navigate(`/integrations/instagram/${ig.integration_id}?tab=inbox&conversation_id=${ig.conversation_id}`);
                              }}
                              className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 truncate flex items-center justify-center"
                              title="Open Instagram Conversation"
                            >
                              <Instagram className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                              <span className="hidden md:inline">Open Chat</span>
                              <span className="md:hidden">Chat</span>
                            </button>
                          )}
                          {query.status === 'closed' ? (
                            <>
                              <button
                                onClick={() => handleReopenQuery(query.query_id)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 truncate"
                                title="Reopen Query"
                              >
                                <span className="hidden md:inline">Reopen Query</span>
                                <span className="md:hidden">Reopen</span>
                              </button>
                              <button
                                onClick={() => openNotesModal(query.query_id)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 truncate"
                                title="Manage Notes"
                              >
                                <span className="hidden md:inline">Manage Notes</span>
                                <span className="md:hidden">Notes</span>
                              </button>
                              <button
                                onClick={() => navigate(`/support-inbox?search_query=${query.query_id}`)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 truncate"
                                title="Open in Support Inbox"
                              >
                                <span className="hidden md:inline">Open in Inbox</span>
                                <span className="md:hidden">Inbox</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleManualResponse(query)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 truncate"
                                title="Edit Response"
                              >
                                <span className="hidden md:inline">Edit Response</span>
                                <span className="md:hidden">Edit</span>
                              </button>
                              <button
                                onClick={() => openNotesModal(query.query_id)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 truncate"
                                title="Manage Notes"
                              >
                                <span className="hidden md:inline">Manage Notes</span>
                                <span className="md:hidden">Notes</span>
                              </button>
                              <button
                                onClick={() => handleCloseQuery(query.query_id)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 truncate"
                                title="Close Query"
                              >
                                <span className="hidden md:inline">Close</span>
                                <span className="md:hidden">Close</span>
                              </button>
                              <button
                                onClick={() => navigate(`/support-inbox?search_query=${query.query_id}`)}
                                className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 truncate"
                                title="Open in Support Inbox"
                              >
                                <span className="hidden md:inline">Open in Inbox</span>
                                <span className="md:hidden">Inbox</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-4">
              {currentQueries.map((query) => (
                <div
                  key={query.query_id}
                  className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-black transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                      <div className="flex items-center flex-wrap gap-2 sm:gap-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedQueries.has(query.query_id)}
                            onChange={() => handleSelectQuery(query.query_id)}
                            className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                          />
                          <span className="text-xs font-bold text-black bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm whitespace-nowrap">
                            #{query.ticket_id || query.query_id || 'N/A'}
                          </span>
                          {query.external_integration?.instagram && (
                            <span className="inline-flex items-center text-xs font-bold text-black bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm whitespace-nowrap">
                              <Instagram className="h-4 w-4 mr-1" /> Instagram
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="p-1.5 sm:p-2 bg-gray-100 rounded-xl group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-200">
                            {getStatusIcon(query.status)}
                          </div>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${getStatusColor(
                              query.status
                            )}`}
                          >
                            {query.status}
                          </span>
                        </div>
                      </div>
                      <span className="text-[11px] sm:text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full whitespace-nowrap border border-gray-100">
                        {new Date(query.created_at).toLocaleDateString()} at{" "}
                        {new Date(query.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>{" "}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-black" />
                            Customer Name:
                          </h4>
                          <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50">
                            {query.name || "N/A"}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-black" />
                            Mobile Number:
                          </h4>
                          <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50">
                            {query.mobile || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-black" />
                          Customer Email:
                        </h4>
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50">
                          {query.customer_email}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <BarChart3 className="h-4 w-4 mr-2 text-black" />
                          Query:
                        </h4>
                        <p className="text-gray-900 bg-gray-50 p-4 rounded-xl border border-gray-200 shadow-sm">
                          {query.query}
                        </p>
                      </div>{" "}
                      {(query.response || query.ai_response) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <CheckCircle className="h-4 w-4 mr-2 text-black" />
                            Reply from AI Support Agent:
                          </h4>
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 p-4 rounded-xl border border-green-200/50 shadow-sm">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({ node, ...props }) => (
                                  <h1
                                    className="text-2xl font-bold text-gray-900 mb-4 mt-6 border-b border-gray-200 pb-2"
                                    {...props}
                                  />
                                ),
                                h2: ({ node, ...props }) => (
                                  <h2
                                    className="text-xl font-semibold text-gray-800 mb-3 mt-5"
                                    {...props}
                                  />
                                ),
                                h3: ({ node, ...props }) => (
                                  <h3
                                    className="text-lg font-medium text-gray-800 mb-2 mt-4"
                                    {...props}
                                  />
                                ),
                                p: ({ node, ...props }) => (
                                  <p
                                    className="text-gray-900 leading-relaxed mb-3"
                                    {...props}
                                  />
                                ),
                                ul: ({ node, ...props }) => <ul style={{ marginLeft: 24, marginBottom: 8, listStyleType: 'disc' }} {...props} />,
                                ol: ({ node, ...props }) => <ol style={{ marginLeft: 24, marginBottom: 8, listStyleType: 'decimal' }} {...props} />,
                                li: ({ node, ...props }) => <li style={{ marginBottom: 4 }} {...props} />,
                                a: ({ node, ...props }) => (
                                  <a
                                    className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors"
                                    {...props}
                                  />
                                ),
                                code: ({ node, inline, ...props }) =>
                                  inline ? (
                                    <code
                                      className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded font-mono text-sm"
                                      {...props}
                                    />
                                  ) : (
                                    <code
                                      className="block bg-gray-800 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto"
                                      {...props}
                                    />
                                  ),
                                pre: ({ node, ...props }) => (
                                  <pre
                                    className="bg-gray-800 rounded-lg mb-4 overflow-hidden"
                                    {...props}
                                  />
                                ),
                                blockquote: ({ node, ...props }) => (
                                  <blockquote
                                    className="border-l-4 border-gray-400 pl-4 italic text-gray-700 bg-gray-50 py-2 rounded-r-lg mb-4"
                                    {...props}
                                  />
                                ),
                              }}
                            >
                              {query?.ai_response || query?.response}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}{" "}
                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 sm:flex sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200/50">
                        {query.external_integration?.instagram && (
                          <button
                            onClick={() => {
                              const ig = query.external_integration.instagram;
                              navigate(`/integrations/instagram/${ig.integration_id}?tab=inbox&conversation_id=${ig.conversation_id}`);
                            }}
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-black text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                            title="Open Instagram Conversation"
                          >
                            <Instagram className="h-4 w-4 mr-2" />
                            <span className="hidden sm:inline">Open Chat</span>
                            <span className="sm:hidden">Open</span>
                          </button>
                        )}
                        {query.status === 'closed' ? (
                          <>
                            {/* Reopen Query Button */}
                            <button
                              onClick={() => handleReopenQuery(query.query_id)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">Reopen Query</span>
                              <span className="sm:hidden">Reopen</span>
                            </button>

                            {/* Manage Notes Button */}
                            <button
                              onClick={() => openNotesModal(query.query_id)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-black text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                              title="Manage Notes"
                            >
                              <PlusCircle className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">Manage Notes</span>
                              <span className="sm:hidden">Notes</span>
                            </button>
                            {/* Open in Inbox Button for Closed Queries */}
                            <button
                              onClick={() => navigate(`/support-inbox?search_query=${query.query_id}`)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                              title="Open in Support Inbox"
                            >
                              <MessageSquare className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">Open in Inbox</span>
                              <span className="sm:hidden">Inbox</span>
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Manual Response Button */}
                            <button
                              onClick={() => handleManualResponse(query)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 font-medium rounded-xl shadow-lg transition-all duration-200 transform text-sm min-w-0 flex-shrink-0 bg-[#16A34A] hover:bg-[#15803D] text-white hover:shadow-xl hover:-translate-y-0.5"
                            >
                              <Edit className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                              <span className="hidden md:inline truncate">
                                {query.ai_response ? "Edit Reply" : "Send Response"}
                              </span>
                              <span className="hidden sm:inline md:hidden truncate">
                                {query.ai_response ? "Edit Response" : "Send Response"}
                              </span>
                              <span className="sm:hidden truncate">Edit</span>
                            </button>

                            {/* Manage Notes Button */}
                            <button
                              onClick={() => openNotesModal(query.query_id)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                              title="Manage Notes"
                            >
                              <PlusCircle className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">Manage Notes</span>
                              <span className="sm:hidden">Notes</span>
                            </button>

                            {/* Close Query Button */}
                            <button
                              onClick={() => handleCloseQuery(query.query_id)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">Close Query</span>
                              <span className="sm:hidden">Close</span>
                            </button>
                            {/* Open in Inbox Button */}
                            <button
                              onClick={() => navigate(`/support-inbox?search_query=${query.query_id}`)}
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:bg-blue-800 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                              title="Open in Support Inbox"
                            >
                              <MessageSquare className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">Open in Inbox</span>
                              <span className="sm:hidden">Inbox</span>
                            </button>
                          </>
                        )}
                      </div>
                      {/* Display Human Responses if any */}
                      {query.human_response && query.human_response.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Send className="h-4 w-4 mr-2 text-purple-500" />
                            Manual Responses:
                          </h4>
                          <div className="space-y-2">
                            {query.human_response.map((response, index) => (
                              <div
                                key={index}
                                className="bg-gradient-to-r from-purple-50 to-indigo-50/50 p-4 rounded-xl border border-purple-200/50 shadow-sm"
                              >
                                <p className="text-gray-900 leading-relaxed">
                                  {response}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Query Metadata Panel - COMPLETE VERSION */}
                      {(() => {
                        // Robust tag check
                        const hasTags = query.tags && (
                          (Array.isArray(query.tags) && query.tags.length > 0) ||
                          (typeof query.tags === 'string' && query.tags.trim().length > 0)
                        );

                        const hasMetadata =
                          query.classification?.primary_category ||
                          query.classification?.intent ||
                          query.classification?.product_area ||
                          query.sentiment?.emotion ||
                          query.sentiment?.overall ||
                          query.urgency?.level ||
                          query.root_cause?.category ||
                          query.root_cause?.specific_issue ||
                          hasTags;

                        if (!hasMetadata) return null;

                        const urgencyConfig = getUrgencyConfig(query.urgency?.level);

                        return (
                          <div className="mt-4 pt-4 border-t border-gray-200/50 space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-xs font-semibold text-gray-600 flex items-center">
                                <FileText className="h-3.5 w-3.5 mr-1.5 text-gray-600" />
                                Query Analysis
                              </h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Tags - FIRST PRIORITY
                              {hasTags && (
                                <div className="flex items-start space-x-2 md:col-span-2">
                                  <Tag className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                                  <span className="text-xs text-gray-600 font-medium">Tags:</span>
                                  <div className="flex flex-wrap gap-1 flex-1">
                                    {(Array.isArray(query.tags) ? query.tags : query.tags.split(',')).map((tag, index) => {
                                      const tagValue = typeof tag === 'string' ? tag.trim() : tag;
                                      // Skip empty tags
                                      if (!tagValue) return null;

                                      return (
                                        <span
                                          key={index}
                                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                                        >
                                          {tagValue}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )} */}

                              {/* Primary Category */}
                              {query.classification?.primary_category && (
                                <div className="flex items-center space-x-2">
                                  <Tag className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                  <span className="text-xs text-gray-600 font-medium">Category:</span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getCategoryColor(query.classification.primary_category)}`}>
                                    {formatLabel(query.classification.primary_category)}
                                  </span>
                                </div>
                              )}

                              {/* Intent */}
                              {query.classification?.intent && (
                                <div className="flex items-start sm:items-center flex-wrap gap-2">
                                  <Target className="h-4 w-4 text-gray-500 flex-shrink-0 mt-1 sm:mt-0" />
                                  <span className="text-xs text-gray-600 font-medium">Intent:</span>
                                  <div className="flex flex-wrap gap-1.5">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getIntentColor(query.classification.intent)}`}>
                                      {formatLabel(query.classification.intent)}
                                    </span>
                                    {query.classification.product_area && (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getIntentColor(query.classification.intent)}`}>
                                        {formatLabel(query.classification.product_area)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Sentiment */}
                              {(query.sentiment?.emotion || query.sentiment?.overall) && (
                                <div className="flex items-center space-x-2">
                                  <TrendingUp className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                  <span className="text-xs text-gray-600 font-medium">Sentiment:</span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 border border-gray-200">
                                    <span className="text-base mr-1">{getSentimentEmoji(query.sentiment?.emotion || query.sentiment?.overall)}</span>
                                    {formatLabel(query.sentiment?.emotion || query.sentiment?.overall)}
                                  </span>
                                </div>
                              )}

                              {/* Urgency */}
                              {query.urgency?.level && (
                                <div className="flex items-center space-x-2">
                                  <AlertTriangle className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                  <span className="text-xs text-gray-600 font-medium">Urgency:</span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${urgencyConfig.color}`}>
                                    <span className="mr-1">{urgencyConfig.icon}</span>
                                    {urgencyConfig.label}
                                  </span>
                                </div>
                              )}

                              {/* Root Cause */}
                              {query.root_cause?.category && (
                                <div className="flex items-start space-x-2 md:col-span-2">
                                  <Zap className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                                  <span className="text-xs text-gray-600 font-medium">Root Cause:</span>
                                  <span className="text-xs text-black bg-white border border-gray-300 px-2 py-0.5 rounded-md flex-1">
                                    {formatLabel(query.root_cause.specific_issue)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}{" "}

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-2 sm:px-4">
          <div className="flex items-center text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
            <span>Showing {(pagination.current_page - 1) * itemsPerPage + 1}-{Math.min(pagination.current_page * itemsPerPage, totalItems)} of {totalItems}</span>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2 order-1 sm:order-2">
            <button
              onClick={handlePrevPage}
              disabled={!pagination.has_previous}
              className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ChevronLeft className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Prev</span>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {[...Array(pagination.total_pages)].map((_, index) => {
                const page = index + 1;
                const isActive = page === currentPage;

                // On mobile, show fewer page numbers
                const isMobile = window.innerWidth < 640;
                const shouldShow = isMobile
                  ? (page === 1 || page === pagination.total_pages || page === currentPage)
                  : (page === 1 || page === pagination.total_pages || (page >= currentPage - 1 && page <= currentPage + 1));

                if (shouldShow) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                        ? 'bg-black text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                        }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  (page === currentPage - 2 && currentPage > 3) ||
                  (page === currentPage + 2 && currentPage < pagination.total_pages - 2)
                ) {
                  return (
                    <span key={page} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={handleNextPage}
              disabled={!pagination.has_next}
              className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4 sm:ml-1" />
            </button>
          </div>
        </div>
      )}
      {/*Download CSV Modal*/}
      {showDownloadCSVModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md transform transition-all duration-300 scale-100">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Apply filters
                  </h3>
                  <button
                    onClick={() => {
                      setShowDownloadCSVModal(false);
                      clearAll();
                      setEndDate("");
                      setStartDate("");
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleDownloadCSVSubmit} className="space-y-5">
                  {/* Checkbox UI */}
                  <div className="p-4 border rounded-xl w-full max-w-sm bg-white shadow-sm">
                    <p className="mb-2 font-semibold">Filter by Status:</p>

                    <div className="flex items-center space-x-4 mb-3">
                      <button
                        type="button"
                        onClick={selectAll}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={clearAll}
                        className="text-sm text-red-500 hover:underline"
                      >
                        Clear All
                      </button>
                    </div>

                    {/* Checkbox elements */}
                    <div className="space-y-2">
                      {statusOptions.map((status) => (
                        <label key={status} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            value={status}
                            checked={selectedStatuses.includes(status)}
                            onChange={() => handleCheckboxToggle(status)}
                            className="accent-blue-500"
                          />
                          <span className="capitalize">{status}</span>
                        </label>
                      ))}
                    </div>
                  </div>


                  {/* Date Range Filter UI */}
                  <div className="p-4 border rounded-xl w-full max-w-sm bg-white shadow-sm">
                    <p className="mb-2 font-semibold">Filter by Date Range:</p>

                    <div className="flex flex-col space-y-4">
                      <div className="flex flex-col">
                        <label htmlFor="start_date" className="text-sm font-medium text-gray-700">Start Date</label>
                        <input
                          type="date"
                          id="start_date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="border rounded-lg p-2 mt-1"
                        />
                      </div>

                      <div className="flex flex-col">
                        <label htmlFor="end_date" className="text-sm font-medium text-gray-700">End Date</label>
                        <input
                          type="date"
                          id="end_date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="border rounded-lg p-2 mt-1"
                        />
                      </div>
                    </div>
                  </div>


                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDownloadCSVModal(false);
                        clearAll();
                        setEndDate("");
                        setStartDate("");
                      }}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                      disabled={submitting}
                    >
                      {submitting ? "Downloading..." : "Download"}
                    </button>
                  </div>
                </form>

              </div>
            </div>{" "}
          </div>
        </div>
      )}

      {/* Test Query Modal */}
      {showTestModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md transform transition-all duration-300 scale-100">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Submit Test Query
                  </h3>
                  <button
                    onClick={() => setShowTestModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleTestSubmit} className="space-y-5">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-black" />
                      Name
                    </label>
                    <input
                      type="text"
                      value={testForm.name}
                      onChange={(e) =>
                        setTestForm({ ...testForm, name: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-black/50 focus:border-black transition-all duration-200"
                      placeholder="Customer Name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-black" />
                      Customer Email
                    </label>
                    <input
                      type="email"
                      value={testForm.email}
                      onChange={(e) =>
                        setTestForm({ ...testForm, email: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-black/50 focus:border-black transition-all duration-200"
                      placeholder="customer@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-black" />
                      Mobile No.
                    </label>
                    <input
                      type="tel"
                      value={testForm.mobile}
                      onChange={(e) =>
                        setTestForm({ ...testForm, mobile: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-black/50 focus:border-black transition-all duration-200"
                      placeholder="Mobile Number"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <BarChart3 className="h-4 w-4 mr-2 text-black" />
                      Query
                    </label>
                    <textarea
                      value={testForm.query}
                      onChange={(e) =>
                        setTestForm({ ...testForm, query: e.target.value })
                      }
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-black/50 focus:border-black transition-all duration-200 resize-none"
                      placeholder="How do I..."
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowTestModal(false)}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Test Query"}
                    </button>
                  </div>
                </form>
              </div>
            </div>{" "}
          </div>
        </div>
      )}
      {/* Manual Response Modal */}
      {manualResponseModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="flex min-h-full items-center justify-center p-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="flex-shrink-0 px-8 py-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Send Manual Response
                    </h3>
                    <button
                      onClick={() =>
                        setManualResponseModal({
                          show: false,
                          query: null,
                          response: "",
                          submitting: false,
                        })
                      }
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="space-y-6">
                    {manualResponseModal.query && (
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Customer Query:
                          </h4>
                          <p className="text-gray-900 text-base leading-relaxed break-words">
                            {manualResponseModal.query.query}
                          </p>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 p-5 rounded-xl border border-gray-200/50">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Customer Email:
                          </h4>
                          <p className="text-gray-900 text-base break-all">
                            {manualResponseModal.query.customer_email}
                          </p>
                        </div>

                        {manualResponseModal.query.ai_response && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 p-5 rounded-xl border border-green-200/50">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Original Reply from AI Support Agent:
                            </h4>
                            <div className="max-h-48 overflow-y-auto">
                              <p className="text-gray-700 text-sm leading-relaxed break-words">
                                {manualResponseModal.query.ai_response}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Send className="h-4 w-4 mr-2 text-black-600" />
                        Your Response:
                      </label>
                      <textarea
                        value={manualResponseModal.response}
                        onChange={(e) =>
                          setManualResponseModal((prev) => ({
                            ...prev,
                            response: e.target.value,
                          }))
                        }
                        rows={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-600 transition-all duration-200 resize-none text-base leading-relaxed"
                        placeholder="Enter your response to the customer..."
                        disabled={manualResponseModal.submitting || manualResponseModal.rewriting}
                      />
                      <div className="flex items-center justify-end mt-3">
                        <button
                          type="button"
                          onClick={handleRewriteManual}
                          disabled={manualResponseModal.submitting || manualResponseModal.rewriting || !manualResponseModal.response.trim()}
                          className="flex items-center px-4 py-2 text-sm font-medium text-black bg-gray-200 hover:bg-300 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                          title="Rewrite and fix grammar"
                        >
                          {manualResponseModal.rewriting ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Rewriting...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Rewrite
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-8 py-6 border-t border-gray-200 bg-gray-50/50">
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      type="button"
                      onClick={() =>
                        setManualResponseModal({
                          show: false,
                          query: null,
                          response: "",
                          submitting: false,
                        })
                      }
                      className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                      disabled={manualResponseModal.submitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitManualResponse}
                      className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-white bg-gray-800 hover:bg-black rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={
                        manualResponseModal.submitting ||
                        !manualResponseModal.response.trim()
                      }
                    >
                      {manualResponseModal.submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>Send Response</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reply Modal */}
      {bulkReplyModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="flex min-h-full items-center justify-center p-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="flex-shrink-0 px-8 py-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        Reply to Multiple Queries
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Send a reply to {selectedQueries.size} selected {selectedQueries.size === 1 ? 'query' : 'queries'}
                      </p>
                    </div>
                    <button
                      onClick={handleCloseBulkReplyModal}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="space-y-6">
                    {/* Info Box */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-900">
                          <p className="font-medium mb-1">Bulk Reply Information</p>
                          <p className="text-blue-700">
                            This reply will be sent to all {selectedQueries.size} selected queries.
                            Each customer will receive the same message via email.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Reply Text Area */}
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Send className="h-4 w-4 mr-2 text-blue-500" />
                        Your Reply Message:
                      </label>
                      <textarea
                        value={bulkReplyModal.reply}
                        onChange={(e) =>
                          setBulkReplyModal((prev) => ({
                            ...prev,
                            reply: e.target.value,
                          }))
                        }
                        rows={10}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-black/50 focus:border-black transition-all duration-200 resize-none text-base leading-relaxed"
                        placeholder="Enter your reply message that will be sent to all selected queries..."
                        disabled={bulkReplyModal.submitting || bulkReplyModal.rewriting}
                      />
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-gray-500">
                          This message will be sent to all selected queries regardless of their current status.
                        </p>
                        <button
                          type="button"
                          onClick={handleRewriteReply}
                          disabled={bulkReplyModal.submitting || bulkReplyModal.rewriting || !bulkReplyModal.reply.trim()}
                          className="flex items-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                          title="Rewrite and fix grammar"
                        >
                          {bulkReplyModal.rewriting ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Rewriting...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Rewrite
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-8 py-6 border-t border-gray-200 bg-gray-50/50">
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      type="button"
                      onClick={handleCloseBulkReplyModal}
                      className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                      disabled={bulkReplyModal.submitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitBulkReply}
                      className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={
                        bulkReplyModal.submitting ||
                        !bulkReplyModal.reply.trim()
                      }
                    >
                      {bulkReplyModal.submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>Send Reply to {selectedQueries.size} {selectedQueries.size === 1 ? 'Query' : 'Queries'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Response Modal */}
      {viewResponseModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="flex min-h-full items-center justify-center p-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="flex-shrink-0 px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Eye className="h-5 w-5 mr-2 text-gray-700" /> View Response
                  </h3>
                  <button
                    onClick={() => setViewResponseModal({ show: false, response: '', query: null })}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                {/* Content */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="space-y-4">
                    {viewResponseModal.query && (
                      <>
                        <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Query:</h4>
                          <p className="text-gray-900 text-base leading-relaxed break-words">{viewResponseModal.query.query}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 p-5 rounded-xl border border-green-200/50">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Response:</h4>
                          <div className="max-h-64 overflow-y-auto text-gray-900 text-base leading-relaxed break-words whitespace-pre-line">
                            {viewResponseModal.response}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Footer */}
                <div className="flex-shrink-0 px-8 py-6 border-t border-gray-200 bg-gray-50/50 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setViewResponseModal({ show: false, response: '', query: null })}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Professional Notes Management Modal */}
      {notesModal.show && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <MessageSquare className="h-6 w-6 mr-3 text-gray-600" />
                    Notes Management
                  </h2>
                  <button
                    onClick={() => setNotesModal({ show: false, queryId: null, notes: [], newNote: "", submitting: false, editingNoteId: null, editingContent: "" })}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">Manage all notes for this support query</p>
              </div>

              {/* Side-by-side content */}
              <div className="flex-1 flex flex-row gap-6 overflow-hidden p-6">
                {/* Left: Add New Note (fixed width on md+, full width on small screens) */}
                <div className="w-full md:w-1/3 flex-shrink-0 bg-gray-50/50 border border-gray-100 rounded-xl p-5 flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <PlusCircle className="h-5 w-5 mr-2 text-gray-600" />
                    Add New Note
                  </h3>

                  <textarea
                    value={notesModal.newNote}
                    onChange={(e) => setNotesModal(prev => ({ ...prev, newNote: e.target.value }))}
                    placeholder="Write your note here..."
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 mb-4 flex-1"
                    disabled={notesModal.submitting}
                  />

                  <div className="flex items-center justify-between mt-2">
                    <div className="text-sm text-gray-500">Signed in as <span className="font-medium text-gray-700">{getCurrentUserName()}</span></div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setNotesModal(prev => ({ ...prev, newNote: "" }))}
                        className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200"
                        disabled={notesModal.submitting}
                      >
                        Clear
                      </button>

                      <button
                        onClick={handleAddNote}
                        disabled={!notesModal.newNote.trim() || notesModal.submitting}
                        className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-black rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {notesModal.submitting ? (
                          <>
                            <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white/30 border-t-white rounded-full inline-block"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <PlusCircle className="h-4 w-4 flex-shrink-0" />
                            Add Note
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: Existing Notes (scrollable) */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2 text-gray-600" />
                      All Notes ({notesModal.notes.length})
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 py-4">
                    {notesModal.notes.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl"></div>
                          <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
                            <MessageSquare className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h4>
                        <p className="text-gray-500">Add your first note using the form on the left</p>
                      </div>
                    ) : (
                      <div className="space-y-4 pb-4">
                        {notesModal.notes.map((note, index) => (
                          <div key={note.id || index} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                            <div className="p-5">
                              {/* Note Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {note.added_by || "Unknown User"}
                                    </p>
                                    <div className="flex items-center text-xs text-gray-500">
                                      <Calendar className="h-3 w-3 mr-1" />
                                      {note.added_at ? new Date(note.added_at).toLocaleString() : "Unknown Date"}
                                      {note.updated_at && (
                                        <span className="ml-2 text-blue-600">(Updated: {new Date(note.updated_at).toLocaleString()})</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Note Actions */}
                                <div className="flex items-center space-x-2">
                                  {notesModal.editingNoteId === note.id ? (
                                    <>
                                      <button
                                        onClick={() => handleSaveEditNote(note.id)}
                                        disabled={!notesModal.editingContent.trim() || notesModal.submitting}
                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                        title="Save"
                                      >
                                        <Save className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                        title="Cancel"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handleEditNote(note.id, note.note)}
                                        className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                        title="Edit Note"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteNote(note.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                        title="Delete Note"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Note Content */}
                              {notesModal.editingNoteId === note.id ? (
                                <textarea
                                  value={notesModal.editingContent}
                                  onChange={(e) => setNotesModal(prev => ({ ...prev, editingContent: e.target.value }))}
                                  rows={4}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                              ) : (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                                    {note.note}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50/50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setNotesModal({ show: false, queryId: null, notes: [], newNote: "", submitting: false, editingNoteId: null, editingContent: "" })}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// 
export default SupportQueries;